import React, { useRef } from 'react'
import { useCookies } from 'react-cookie'

import { Accordion, Col, Container, Row } from 'react-bootstrap'
import { MeetingDeletionSettings } from '../MeetingDeletionSettings'
import { ConsentSettings } from '../ConsentSettings'
import { ComplianceNoticeSettingsForm } from '../ComplianceNotice/ComplianceNoticeSettings'
import { Tooltip } from '../../../../components/Tooltip/Tooltip'
import './MainComplianceSettings.scss'
import { CalculateEmailTypeForConsentSettings } from '../CalculateEmailTypeForConsentSettings/CalculateEmailTypeForConsentSettings'
import { useZocksQuota } from '../../../../components/ScopedBlock/hooks'
import { ConfirmationNoticeSettings } from '../ConfirmationNoticeSettings'
import { MeetingTrimmingSettings } from '../MeetingTrimmingSettings'
import { COMPLIANCE_ACCORDION_ITEMS } from './ComplianceAccordionItems'
import { useAutoScroll } from '../../../../hooks/useAutoScroll'
import { AuditLogSettings } from '../AuditLogSettings'
import { IntegrationServiceId } from '../../../../common/integrations'
import { IntegrationIcon } from '../../../../components/IntegrationIcon'

export interface MainComplianceSettingsProps {
  defaultAccordionItemKey?: string
}

export const MainComplianceSettings = ({ defaultAccordionItemKey }: MainComplianceSettingsProps) => {
  const [cookies] = useCookies(['auditLog'])
  const { hasQuota } = useZocksQuota()
  const emailEnabled = hasQuota('integration.emailEnabled.value')
  const exportNotificationEnabled = hasQuota('integration.exConfirmEnabled.value')

  const isAuditLogCookieEnabled = (cookieValue: unknown): boolean => cookieValue === true
  const auditLogEnabled = isAuditLogCookieEnabled(cookies.auditLog)
  const complianceNoticeRef = useRef<HTMLDivElement>(null)

  useAutoScroll([{ key: COMPLIANCE_ACCORDION_ITEMS.complianceNotice, ref: complianceNoticeRef }], defaultAccordionItemKey)

  return (
    <>
      <Container fluid className='p-0'>
        <Row>
          <Col>
            <Accordion
              defaultActiveKey={defaultAccordionItemKey ? defaultAccordionItemKey : COMPLIANCE_ACCORDION_ITEMS.meetingDeletion}
              flush
              className='compliance-accordion'
            >
              <Accordion.Item eventKey={COMPLIANCE_ACCORDION_ITEMS.meetingDeletion}>
                <Accordion.Header>Meeting Deletion</Accordion.Header>
                <Accordion.Body>
                  <MeetingDeletionSettings />
                </Accordion.Body>
              </Accordion.Item>
              <Accordion.Item eventKey={COMPLIANCE_ACCORDION_ITEMS.meetingTrimming}>
                <Accordion.Header>Meeting Trimming</Accordion.Header>
                <Accordion.Body>
                  <MeetingTrimmingSettings />
                </Accordion.Body>
              </Accordion.Item>
              <Accordion.Item eventKey={COMPLIANCE_ACCORDION_ITEMS.trackingConsent}>
                <Accordion.Header>Tracking Consent for the Use of Zocks</Accordion.Header>
                <Accordion.Body>
                  <ConsentSettings />
                </Accordion.Body>
              </Accordion.Item>
              <Accordion.Item eventKey={COMPLIANCE_ACCORDION_ITEMS.complianceNotice} ref={complianceNoticeRef}>
                <Accordion.Header>
                  <div className='d-flex w-100 flex-row align-items-center gap-3'>
                    Set compliance notice
                    <div>
                      <Tooltip title='Zoom' placement='top'>
                        <IntegrationIcon serviceId={IntegrationServiceId.ZOOM} width={'2rem'} height={'2rem'} className='me-1' />
                      </Tooltip>
                      <Tooltip title='Microsoft Teams' placement='top'>
                        <IntegrationIcon serviceId={IntegrationServiceId.TEAMS} width={'2rem'} height={'2rem'} className='me-1' />
                      </Tooltip>
                      <Tooltip title='Webex by Cisco' placement='top'>
                        <IntegrationIcon serviceId={IntegrationServiceId.WEBEX} width={'2rem'} height={'2rem'} className='me-1' />
                      </Tooltip>
                    </div>
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  <ComplianceNoticeSettingsForm />
                </Accordion.Body>
              </Accordion.Item>
              {emailEnabled ? (
                <Accordion.Item eventKey={COMPLIANCE_ACCORDION_ITEMS.processEmailContent}>
                  <Accordion.Header>Process email content with AI based on Consent</Accordion.Header>
                  <Accordion.Body>
                    <CalculateEmailTypeForConsentSettings />
                  </Accordion.Body>
                </Accordion.Item>
              ) : null}
              {exportNotificationEnabled ? (
                <Accordion.Item eventKey={COMPLIANCE_ACCORDION_ITEMS.confirmationNotice}>
                  <Accordion.Header>Confirmation Notice on Data Exports</Accordion.Header>
                  <Accordion.Body>
                    <ConfirmationNoticeSettings />
                  </Accordion.Body>
                </Accordion.Item>
              ) : null}
              {auditLogEnabled ? (
                <Accordion.Item eventKey={COMPLIANCE_ACCORDION_ITEMS.auditLog}>
                  <Accordion.Header>Audit Log</Accordion.Header>
                  <Accordion.Body>
                    <AuditLogSettings />
                  </Accordion.Body>
                </Accordion.Item>
              ) : null}
            </Accordion>
          </Col>
        </Row>
      </Container>
    </>
  )
}
